import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import classnames from 'classnames'
import map from 'lodash/map'
import filter from 'lodash/filter'
import useClickOutsideListener from '@Utils/UseClickOutsideListener'
import sortSearchResults from '@Utils/SortSearchResults'
import Arrow from '@Images/search_dropdown_arrow.svg'

import './SearchDropdown.scss'

const DROPDOWN_OFFSET_Y = 50 // px
const DEFAULT_NO_OPTION_FOUND_TEXT = 'No option found'

interface SearchDropdownProps {
  readonly availableOptions: string[]
  readonly selectedOption: string
  readonly classNameForClickOutsideListener: string
  readonly idNameOfParentContainer: string
  readonly placeholderText: string
  readonly placeholderTextWhenSelected?: string
  readonly showAlert?: boolean
  readonly mainContainerIdName?: string
  readonly disabled?: boolean
  readonly classNameOfContainerToMakeUnscrollable?: string
  readonly paddingToCompensateScrollbarWhenUnscrollable?: number
  readonly noOptionFoundText?: string
  readonly 'data-testid'?: string
  setSelectedOption(selectedOption: string): void
  setSelectedOptionIndex?(selectedOptionIndex: number): void
  setShowButtons?(newShowButtons: boolean): void
}

const SearchDropdown: React.FC<SearchDropdownProps> = props => {
  const {
    classNameForClickOutsideListener,
    idNameOfParentContainer,
    availableOptions,
    selectedOption,
    showAlert,
    placeholderText,
    placeholderTextWhenSelected,
    mainContainerIdName,
    disabled,
    classNameOfContainerToMakeUnscrollable,
    paddingToCompensateScrollbarWhenUnscrollable,
    noOptionFoundText,
  } = props

  const [inputIsFocused, setInputIsFocused] = useState(false)
  const [relevantOptions, setRelevantOptions] = useState(availableOptions)
  const [searchText, setSearchText] = useState('')
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)

  const optionSelected = selectedOption.length > 0
  const showSearchInput = showSearchDropdown || !optionSelected
  const hasRelevantOptions = relevantOptions.length > 0
  const dataTestId = props['data-testid']

  const inputRef = useRef<HTMLInputElement>(null)
  const arrowRef = useRef<HTMLSpanElement>(null)

  useClickOutsideListener(classNameForClickOutsideListener, setShowSearchDropdown, showSearchDropdown)

  useEffect(() => {
    const filtered = filter(availableOptions, option =>
      option.toLowerCase().includes(searchText.toLowerCase())
    )

    const sorted = filtered.sort(sortSearchResults(searchText))

    setRelevantOptions(sorted)
  }, [availableOptions, searchText])

  useEffect(() => {
    if (showSearchDropdown && inputRef.current) {
      inputRef.current.focus()
      setInputIsFocused(true)
    }

    if (!showSearchDropdown) {
      setSearchText('')
    }
  }, [showSearchDropdown])

  useEffect(() => {
    if (!props.setShowButtons) { return }

    const newShowButtons = optionSelected && !showSearchInput
    props.setShowButtons(newShowButtons)
  }, [optionSelected, showSearchInput])

  useEffect(() => {
    if (classNameOfContainerToMakeUnscrollable) {
      const container = document.getElementsByClassName(classNameOfContainerToMakeUnscrollable)
      if (container && container.length > 0) {
        const containerElement = container[0] as HTMLElement
        containerElement.style.overflowY = showSearchDropdown ? 'hidden' : 'scroll'
        if (typeof(paddingToCompensateScrollbarWhenUnscrollable) === 'number') {
          containerElement.style.paddingRight = showSearchDropdown ? paddingToCompensateScrollbarWhenUnscrollable + 'px' : '0px'
        }
      }
    }
  }, [showSearchDropdown])

  const handleSetFocus = () => {
    setInputIsFocused(true)
  }

  const handleRemoveFocus = () => {
    setInputIsFocused(false)
  }

  const handleSearchTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchText = (e.target as HTMLInputElement).value
    setSearchText(newSearchText)
  }

  const handleOnClickSearch = (e: React.MouseEvent) => {
    if (arrowRef.current && arrowRef.current.contains(e.target as Node)) {
      return
    }

    if (!showSearchDropdown) {
      setShowSearchDropdown(true)
    }
  }

  const openSearchDropdown = () => {
    if (disabled) { return }

    setShowSearchDropdown(true)
  }

  const handleArrowClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowSearchDropdown(prev => !prev)
  }

  const optionsList = map(relevantOptions, (relevantOption, index) => {
    const handleSetSelectedOption = () => {
      props.setSelectedOption(relevantOption)
      if (props.setSelectedOptionIndex) {
        props.setSelectedOptionIndex(index)
      }
      setShowSearchDropdown(false)
    }

    return (
      <li
        key={index}
        className="SearchDropdown__item"
        onClick={handleSetSelectedOption}
      >
        <div className="SearchDropdown__item-name">
          {relevantOption}
        </div>
      </li>
    )
  })

  const renderNoOptionFound = () => {
    return (
      <li className="SearchDropdown__item SearchDropdown__item-not-found">
        <div className="SearchDropdown__item-name">
          {noOptionFoundText ? noOptionFoundText : DEFAULT_NO_OPTION_FOUND_TEXT}
        </div>
      </li>
    )
  }

  const dropdownParentElement = document.getElementById(idNameOfParentContainer)
  let dropdownX = 0
  let dropdownY = 0

  if (dropdownParentElement) {
    const dropdownParentElementBounds = dropdownParentElement.getBoundingClientRect()
    dropdownX = dropdownParentElementBounds.left
    dropdownY = dropdownParentElementBounds.top + DROPDOWN_OFFSET_Y
  }

  const dropdownPositioningStyle = {
    top: `${dropdownY}px`,
    left: `${dropdownX}px`,
  }

  const renderSearchDropdown = () => {
    return ReactDOM.createPortal(
      (
        <ul
          className={`SearchDropdown__items ${classNameForClickOutsideListener}`}
          style={dropdownPositioningStyle}
        >
          {hasRelevantOptions ? optionsList : renderNoOptionFound()}
        </ul>
      ),
      document.getElementById(mainContainerIdName || 'site-container')!
    )
  }

  const renderSearchInput = () => {
    return (
      <div
        className={classnames(
          'SearchDropdown__input-wrapper',
          { 'SearchDropdown__input-wrapper-focused': inputIsFocused },
          { 'SearchDropdown__input-wrapper-alert': showAlert && !inputIsFocused }
        )}
        onClick={handleOnClickSearch}
        data-testid={dataTestId ? `${dataTestId}-search-input` : undefined}
      >
        <input
          ref={inputRef}
          placeholder={placeholderTextWhenSelected && selectedOption ? placeholderTextWhenSelected : placeholderText}
          autoComplete="off"
          value={searchText}
          onChange={handleSearchTextChange}
          onFocus={handleSetFocus}
          onBlur={handleRemoveFocus}
        />
        <span ref={arrowRef}>
          <Arrow
            onClick={handleArrowClick}
          />
        </span>
      </div>
    )
  }

  const renderCurrentItem = () => {
    return (
      <div
        className={classnames(
          'SearchDropdown__current-item',
          { 'SearchDropdown__current-item-disabled': disabled }
        )}
        onClick={openSearchDropdown}
        data-testid={dataTestId}
      >
        <div className="SearchDropdown__current-item-name">
          {selectedOption}
        </div>
        <Arrow />
      </div>
    )
  }

  return (
    <React.Fragment>
      {showSearchInput ? renderSearchInput() : renderCurrentItem()}
      {showSearchDropdown && renderSearchDropdown()}
    </React.Fragment>
  )
}

export default SearchDropdown
